import { MapPin } from 'lucide-react';
import type { GrowingRegionDraft } from './growingRegionDraft';
import { draftHemisphere } from './growingRegionDraft';

// The field set for a growing region, shared by the inline picker and the
// admin manager so the two cannot drift on validation hints or wording.

interface Props {
  value: GrowingRegionDraft;
  onChange: (next: GrowingRegionDraft) => void;
  disabled?: boolean;
  /** Tighter spacing and smaller text for the inline-in-a-form case. */
  compact?: boolean;
}

export function GrowingRegionFields({ value, onChange, disabled, compact }: Props) {
  const set = (patch: Partial<GrowingRegionDraft>) => onChange({ ...value, ...patch });
  const labelClass = compact
    ? 'block text-xs font-medium text-neutral-700 mb-1'
    : 'block text-sm font-medium text-neutral-700 mb-1';
  const inputClass = compact
    ? 'input-professional w-full'
    : 'w-full px-3 py-2 border border-neutral-300 rounded-md text-sm';
  const hemisphere = draftHemisphere(value);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={labelClass}>Region name *</label>
          <input
            className={inputClass}
            value={value.name}
            disabled={disabled}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Goulburn Valley"
          />
          <p className="text-[11px] text-neutral-500 mt-1">
            A climate area that harvests together — not a state or a licensing territory.
            Batlow and Sydney are both AU-NSW and pick weeks apart.
          </p>
        </div>

        <div>
          <label className={labelClass}>Country</label>
          <input
            className={inputClass}
            value={value.country}
            disabled={disabled}
            onChange={(e) => set({ country: e.target.value })}
            placeholder="Australia"
          />
        </div>

        <div>
          <label className={labelClass}>ISO 3166-2 code</label>
          <input
            className={inputClass}
            value={value.region_code}
            disabled={disabled}
            onChange={(e) => set({ region_code: e.target.value.toUpperCase() })}
            placeholder="AU-VIC"
          />
          <p className="text-[11px] text-neutral-500 mt-1">
            Optional, for cross-referencing licensing territories only.
          </p>
        </div>

        <div>
          <label className={labelClass}>Centre latitude</label>
          <input
            className={inputClass}
            type="number"
            step="any"
            value={value.centroid_latitude}
            disabled={disabled}
            onChange={(e) => set({ centroid_latitude: e.target.value })}
            placeholder="-36.38"
          />
        </div>

        <div>
          <label className={labelClass}>Centre longitude</label>
          <input
            className={inputClass}
            type="number"
            step="any"
            value={value.centroid_longitude}
            disabled={disabled}
            onChange={(e) => set({ centroid_longitude: e.target.value })}
            placeholder="145.40"
          />
        </div>

        <div>
          <label className={labelClass}>Elevation (m)</label>
          <input
            className={inputClass}
            type="number"
            value={value.elevation_m}
            disabled={disabled}
            onChange={(e) => set({ elevation_m: e.target.value })}
            placeholder="110"
          />
        </div>

        <div className="flex items-end">
          <p className="text-[11px] text-neutral-500 flex items-start gap-1 pb-2">
            <MapPin className="h-3.5 w-3.5 mt-px shrink-0" />
            <span>
              The centre point sets the hemisphere
              {hemisphere && (
                <>
                  {' '}(
                  <span className="font-medium">
                    {hemisphere === 'S' ? 'southern' : 'northern'}
                  </span>
                  )
                </>
              )}
              , and lets a GPS fix find this region. Without it, timing comparisons
              across hemispheres cannot be excluded.
            </span>
          </p>
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass}>Notes</label>
          <input
            className={inputClass}
            value={value.notes}
            disabled={disabled}
            onChange={(e) => set({ notes: e.target.value })}
            placeholder="Anything that characterises the district's timing"
          />
        </div>
      </div>
    </div>
  );
}

export default GrowingRegionFields;
